import { createAction, createSlice, current } from '@reduxjs/toolkit';
import { hidePanelLoading, showPanelLoading, changeTableRow, hidePanel, setFilter } from '@/pages/shared/shared.reducer';
import { runUserProfileMutation, runUserProfileQuery } from '@/common/user-profile/user-profile.query';
import { isUnauthorizedError } from '@/pages/shared/rtk-query-helpers'
import { openSuccessNotification, openErrorNotification } from '@/notification';
import { CHANGES_ARE_SAVED, NEW_USER_CREATED } from '@/constants/constant.string';
import panels from '@/constants/panels';
import { getSetting, getUserPermissions, setSettings } from '@/pages/login/login.reducer';
import { config } from '@/config';
import { BASE_USER } from '@/constants/roles';

const LOGIN_DETAILS_VALUE_SET = "LOGIN_DETAILS_VALUE_SET";
const STAFF_DETAILS_VALUE_SET = "STAFF_DETAILS_VALUE_SET";
const STAFF_DETAILS_LOADING_STATE_SET = "STAFF_DETAILS_LOADING_STATE_SET";
const STAFF_DETAILS_SUCCESS = "STAFF_DETAILS_SUCCESS";
const STAFF_DETAILS_CLEAR = "STAFF_DETAILS_CLEAR";
const SET_DASHBOARD_LIST = 'SET_DASHBOARD_LIST';
const SET_VALUE = 'SET_VALUE_USER_PROFILE';
const LOGIN_DETAILS_EDIT_WITH_BASE = 'LOGIN_DETAILS_EDIT_WITH_BASE';
const SET_EDIT_DATA_AS_DATA_USER = 'SET_EDIT_DATA_AS_DATA_USER';

const contactInitialState = {
    loading: false,
    myProfileLoadError: false,
    myProfileLoaded: false,
    dashboardList: [],
    data: {
        countryId: 1,
        user: { receiveNotifications: true, defaultPageSize: 150 }
    },
    editData: {
        countryId: 1,
        user: { receiveNotifications: true, defaultPageSize: 150 }
    },
    defaultSites: [],
    email: undefined
}

const getApiErrorMessage = (error, fallbackMessage = 'Unknown error') => {
    const responseData = error?.data ?? error?.response?.data;
    const responseMessage = typeof responseData === 'string'
        ? responseData
        : responseData?.message || responseData?.error;
    const statusText = error?.response?.statusText;
    const directMessage = error?.message;

    if (responseMessage && !/undefined/i.test(responseMessage)) {
        return responseMessage;
    }

    if (statusText && !/undefined/i.test(statusText)) {
        return statusText;
    }

    if (directMessage && !/undefined/i.test(directMessage) && !/^request failed/i.test(directMessage)) {
        return directMessage;
    }

    return fallbackMessage;
};

// Reflects a just-saved appearance theme into userDetails.setting immediately,
// ahead of the async getSetting() refresh, so the theme runtime's saved value
// (and any active preview watching it) update without waiting on a round trip.
const applySavedAppearanceTheme = (dispatch, getState, model, data) => {
    const appearanceTheme = data?.user?.appearanceTheme ?? model?.user?.appearanceTheme;
    const currentSettings = getState()?.userDetails?.setting;

    if (currentSettings && appearanceTheme) {
        dispatch(setSettings({
            ...currentSettings,
            appearanceTheme,
        }));
    }
};

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const setContactDetailsValue = createAction(STAFF_DETAILS_VALUE_SET, (inputName, value) => ({
    payload: { name: inputName, value }
}));

export const setLoginDetailsValue = createAction(LOGIN_DETAILS_VALUE_SET, (inputName, value) => ({
    payload: { name: inputName, value }
}));

export const changeItemUser = createAction(LOGIN_DETAILS_EDIT_WITH_BASE, (key, value) => ({
    payload: { name: key, value }
}));

export const setValue = createAction(SET_VALUE, (key, value) => ({
    payload: { key, value }
}));

export const clearContactDetails = createAction(STAFF_DETAILS_CLEAR);

export const setEditDataToDefault = createAction(SET_EDIT_DATA_AS_DATA_USER);

export const passwordResetDetailsReceived = createAction('PASSWORD_RESET_DETAILS_RECEIVED');

const setDashboardList = createAction(SET_DASHBOARD_LIST, (data) => ({ payload: data }));

const setContactDetailsLoadingState = createAction(STAFF_DETAILS_LOADING_STATE_SET, (loading) => ({ payload: loading }));

const getContactDetailsSuccess = createAction(STAFF_DETAILS_SUCCESS, (response) => ({
    payload: { data: response }
}));

// Replaces immutability-helper `update.set`: assigns a dotted path, creating missing objects.
const setPathValue = (draft, path, value) => {
    const segments = String(path).split('.');
    let target = draft;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];

        if (target[segment] === null || typeof target[segment] !== 'object') {
            target[segment] = {};
        }

        target = target[segment];
    }

    target[segments[segments.length - 1]] = value;
};

const contactDetailsSlice = createSlice({
    name: 'contactDetails',
    initialState: contactInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(passwordResetDetailsReceived, (draft, { payload: user }) => {
                if (draft.data.user?.userId !== user.userId || draft.editData.user?.userId !== user.userId) {
                    return;
                }

                if (draft.data.user.lastPasswordRequestAt > user.lastPasswordRequestAt) {
                    return;
                }

                for (const key of ['passwordResetAt', 'lastPasswordRequestAt', 'lastPasswordRequestMethod', 'passwordResetLinkExpiresAt']) {
                    draft.data.user[key] = user[key];
                    draft.editData.user[key] = user[key];
                }
            })
            .addCase(setValue, (draft, action) => {
                setPathValue(draft, action.payload.key, action.payload.value);
            })
            .addCase(setContactDetailsValue, (draft, action) => {
                draft.editData[action.payload.name] = action.payload.value;
            })
            .addCase(setContactDetailsLoadingState, (draft, action) => {
                draft.loading = action.payload;
            })
            .addCase(getContactDetailsSuccess, (draft, action) => {
                draft.loading = false;
                draft.data = { ...action.payload.data };
                draft.editData = { ...action.payload.data };
            })
            .addCase(clearContactDetails, (draft) => {
                const snapshot = current(draft);

                return {
                    ...contactInitialState,
                    loading: snapshot.loading,
                    defaultSites: snapshot.defaultSites,
                    dashboardList: snapshot.dashboardList,
                    alternativeUsers: snapshot.alternativeUsers
                };
            })
            .addCase(setLoginDetailsValue, (draft, action) => {
                // Legacy spread tolerated a missing `user`; Immer needs it to exist first.
                if (!draft.editData.user) {
                    draft.editData.user = {};
                }

                draft.editData.user[action.payload.name] = action.payload.value;
            })
            .addCase(changeItemUser, (draft, action) => {
                if (!draft.editData.user) {
                    draft.editData.user = {};
                }

                if (!draft.data.user) {
                    draft.data.user = {};
                }

                draft.editData.user[action.payload.name] = action.payload.value;
                draft.data.user[action.payload.name] = action.payload.value;
            })
            .addCase(setDashboardList, (draft, action) => {
                draft.dashboardList = action.payload;
            })
            .addCase(setEditDataToDefault, (draft) => {
                draft.editData = { ...current(draft).data };
            });
    }
});

export const contactDetailsReducer = contactDetailsSlice.reducer;

export const getContactDetails = (contactId) => {
    return (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));
        dispatch(showPanelLoading(panels.systemUser));

        runUserProfileQuery(dispatch, 'getContactDetails', contactId)
            .then((data) => {
                dispatch(getContactDetailsSuccess(data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't load contact details. Please refresh page`);
                }
            })
            .finally(() => {
                dispatch(setContactDetailsLoadingState(false));
                dispatch(hidePanelLoading(panels.systemUser));
            });
    }
}

export const loadStaffDetails = (id) => {
    return (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));
        dispatch(showPanelLoading(panels.systemUser));

        runUserProfileQuery(dispatch, 'getStaff', id)
            .then((data) => {
                dispatch(getContactDetailsSuccess(data));
                dispatch(setDefaultLoginRole(100));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't load staff details. Please reload page`);
                }
            })
            .finally(() => {
                dispatch(setContactDetailsLoadingState(false));
                dispatch(hidePanelLoading(panels.systemUser));
            });
    }
}

export const getDetailsByUserId = (id) => {
    return (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));
        dispatch(showPanelLoading(panels.systemUser));

        runUserProfileQuery(dispatch, 'getDetailsByUserId', id)
            .then((data) => {
                dispatch(getContactDetailsSuccess(data));
                dispatch(setDefaultLoginRole(100));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't load user details. Please reload page`);
                }
            })
            .finally(() => {
                dispatch(setContactDetailsLoadingState(false));
                dispatch(hidePanelLoading(panels.systemUser));
            });
    }
}

export const getDashboards = () => {
    return (dispatch) => {
        runUserProfileQuery(dispatch, 'getDashboards')
            .then(data => {
                dispatch(setDashboardList(data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't get dashboards. Please reload page`);
                }
            });
    }
}

export const getCurrentUserProfile = () => {
    return (dispatch) => {
        dispatch(setValue('myProfileLoadError', false));
        dispatch(setValue('myProfileLoaded', false));
        dispatch(setContactDetailsLoadingState(true));

        runUserProfileQuery(dispatch, 'getMyUserProfile')
            .then(data => {
                dispatch(getContactDetailsSuccess(data));
                dispatch(setValue('myProfileLoaded', true));
            })
            .catch(error => {
                dispatch(setValue('myProfileLoadError', true));
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't load profile information. Please reload page`);
                }
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setContactDetailsLoadingState(false));
                }, config.LOADING_DELAY)
            })
    }
}

export const saveContactDetails = (model, tableId, needClose, isCurrentUser, userNameWasChanged, siteId) => {
    return (dispatch, getState) => {
        dispatch(setContactDetailsLoadingState(true));

        return runUserProfileMutation(dispatch, 'saveContactDetails', model)
            .then((data) => {
                openSuccessNotification(model.user.userId ? CHANGES_ARE_SAVED : NEW_USER_CREATED);
                dispatch(setFilter('accountHistoryTable', 'refreshKey', Date.now()));

                if (isCurrentUser) {
                    applySavedAppearanceTheme(dispatch, getState, model, data);
                    dispatch(getSetting());
                    dispatch(getUserPermissions());
                }

                if (!needClose) {
                    dispatch(getContactDetailsSuccess(data));
                }
                else {
                    dispatch(clearContactDetails());
                    dispatch(hidePanel(panels.userProfile));
                    dispatch(hidePanel(panels.systemUser))
                    dispatch(hidePanel(panels.siteUser))
                }
                if (/siteUsersTable.*/.test(tableId) && data.userSites && data.userSites.length > 0) {
                    let role = data.userSites.filter(item => item.siteId == siteId);
                    if (role.length > 0) {
                        tableId && dispatch(changeTableRow(tableId, { ...data, ...data.user, roleId: role[0].roleId }, 'contactId'))
                    }
                    else {
                        tableId && dispatch(changeTableRow(tableId, { ...data, ...data.user, roleId: null }, 'contactId'))
                    }
                }
                else {
                    tableId && dispatch(changeTableRow(tableId, { ...data, ...data.user, roleId: model.user.userRoleId }, 'contactId'))
                }
                return data;
            })
            .catch(error => {
                const errorMessage = getApiErrorMessage(error);
                
                console.error("Save contact error details:", {
                    status: error?.status,
                    message: errorMessage,
                    responseData: error?.data,
                    stackTrace: error?.data?.stackTrace
                });
                
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Failed to save contact: ${errorMessage}`);
                }
                return false;
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setContactDetailsLoadingState(false));
                }, config.LOADING_DELAY)
            });
    }
}

export const getDefaultSites = (accountId, contactId, isNew, notChooseFirst) => {
    return (dispatch) => {
        runUserProfileQuery(dispatch, 'getDefaultSites', { accountId, contactId })
            .then(sites => {
                const sortedSites = [...sites].sort((a, b) => a.siteFullName.toLowerCase() > b.siteFullName.toLowerCase() ? 1 : -1);
                dispatch(setValue('defaultSites', sortedSites));
                dispatch(setValue('data.accountId', accountId));
                dispatch(setValue('editData.accountId', accountId));

                if (!notChooseFirst && sortedSites.length > 0 && isNew) {
                    dispatch(setValue('data.user.defaultSiteId', sortedSites[0].siteId));
                    dispatch(setValue('editData.user.defaultSiteId', sortedSites[0].siteId));
                }
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't load site list. Please reload page`);
                }
            });
    }
}

export const buildNewUserSitePermissions = (availableSites, defaultSites, siteId) => {
    const defaultSiteIds = new Set((defaultSites || []).map(site => site.siteId));

    return (availableSites || [])
        .filter(site => !siteId || site.siteId == siteId)
        .map(site => {
            const hasDefaultAccess = siteId ? true : defaultSiteIds.has(site.siteId);

            return {
                userId: 0,
                siteId: site.siteId,
                siteName: site.siteFullName,
                roleId: hasDefaultAccess ? BASE_USER : 0,
                roleName: hasDefaultAccess ? 'Base User' : null,
                role: hasDefaultAccess ? 'Base User' : null,
                rolename: hasDefaultAccess ? 'Base User' : null,
                hasAccess: hasDefaultAccess,
                adUser: false,
                active: true,
            };
        });
};

export const getNewUserSitePermissions = (accountId, siteId = null) => {
    return async (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));

        try {
            const requests = [runUserProfileQuery(dispatch, 'getAvailableSites', accountId)];
            if (!siteId) {
                requests.push(runUserProfileQuery(dispatch, 'getDefaultSites', { accountId, contactId: null }));
            }

            const [availableSites, defaultSites] = await Promise.all(requests);
            const userSites = buildNewUserSitePermissions(availableSites, defaultSites || [], siteId);

            dispatch(setValue('data.userSites', userSites));
            dispatch(setValue('editData.userSites', userSites));
        }
        catch (error) {
            if (!isUnauthorizedError(error)) {
                openErrorNotification(`Can't load user site permissions. Please reload page`);
            }
        }
        finally {
            dispatch(setContactDetailsLoadingState(false));
        }
    };
};

export const saveStaffDetails = (model, tableId, needClose, isCurrentUser) => {
    return (dispatch, getState) => {
        dispatch(setContactDetailsLoadingState(true));

        return runUserProfileMutation(dispatch, 'saveStaffDetails', model)
            .then((data) => {
                openSuccessNotification(model.user.userId ? CHANGES_ARE_SAVED : NEW_USER_CREATED);

                dispatch(clearContactDetails());

                if (isCurrentUser) {
                    applySavedAppearanceTheme(dispatch, getState, model, data);
                    dispatch(getSetting());
                    dispatch(getUserPermissions());
                    // response.data.user.defaultSiteId && dispatch(setLastSiteId(response.data.user.defaultSiteId));
                }

                if (!needClose) {
                    dispatch(getContactDetailsSuccess(data));
                }
                else {
                    dispatch(hidePanel(panels.userProfile));
                    dispatch(hidePanel(panels.systemUser));
                    dispatch(hidePanel(panels.siteUser))
                }

                tableId && dispatch(changeTableRow(tableId, { ...data, ...data.user }, 'staffId'))
                return data;
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Error. Can't save staff details`);
                }
                return false;
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setContactDetailsLoadingState(false));
                }, config.LOADING_DELAY)
            });
    }
}

export const deleteContact = (item, tableId, isFilterActive, hideNotification = false) => {
    return (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));

        runUserProfileQuery(dispatch, 'canDeleteUser', item.userId)
            .then(check => {
                if (check.canDelete) {
                    runUserProfileMutation(dispatch, 'deleteContact', item.contactId)
                        .then(data => {
                            !hideNotification && openSuccessNotification(`Contact ${item.contactName} is disabled`);
                            dispatch(setValue('data', data));
                            dispatch(setValue('editData', data));
                            dispatch(changeTableRow(tableId, { ...data, adUser: data.user.adUser, roleId: data.user.userRoleId }, 'contactId', isFilterActive == "true"));
                        })
                        .catch(error => {
                            if (!isUnauthorizedError(error)) {
                                openErrorNotification(getApiErrorMessage(error, `Error. Can't delete ${item.contactName} contact`));
                            }
                        });
                }
                else {
                    openErrorNotification(`Can't delete user. ${check.errorMessages.join(' ')}`)
                }
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(getApiErrorMessage(error, `Some problem. Can't delete contact ${item.contactName}`));
                }
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setContactDetailsLoadingState(false));
                }, config.LOADING_DELAY)
            })
    }
}

export const deleteStaff = (staff, tableId, isFilterActive) => {
    return (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));

        runUserProfileQuery(dispatch, 'canDeleteUser', staff.userId)
            .then(check => {
                if (check.canDelete) {
                    runUserProfileMutation(dispatch, 'deleteStaff', staff.staffId)
                        .then(data => {
                            openSuccessNotification(`${staff.fullName} was deleted`);
                            dispatch(changeTableRow(tableId, { ...data, adUser: data.user.adUser }, 'staffId', isFilterActive == "true"));
                            dispatch(setValue('data', data));
                            dispatch(setValue('editData', data));
                        })
                        .catch(error => {
                            if (!isUnauthorizedError(error)) {
                                openErrorNotification("Error. Can't delete staff");
                            }
                        })
                }
                else {
                    openErrorNotification("You can't delete staff");
                }
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Error. Can't delete staff");
                }
            })
            .finally(() => {
                setTimeout(() => {
                    dispatch(setContactDetailsLoadingState(false));
                }, config.LOADING_DELAY);
            })
    }
}

export const restoreContact = (contactId, tableId, isFilterActive) => {
    return (dispatch, getState) => {
        dispatch(setContactDetailsLoadingState(true));

        return runUserProfileMutation(dispatch, 'restoreContact', contactId)
            .then(data => {
                const existingRow = getState().shared?.[tableId]?.data?.find(item => item.contactId === contactId);
                const siteId = tableId?.startsWith('siteUsersTable-') ? Number(tableId.slice('siteUsersTable-'.length)) : null;
                const roleId = siteId === null ? data.user.userRoleId : data.userSites.find(site => site.siteId === siteId)?.roleId || null;
                dispatch(changeTableRow(tableId, {
                    ...existingRow,
                    ...data,
                    adUser: data.user.adUser,
                    azureUser: data.user.azureUser,
                    suspendAccess: data.user.suspendAccess,
                    roleId,
                }, 'contactId', (isFilterActive === false || isFilterActive === "false")));
                openSuccessNotification('Contact is reactivated');
                dispatch(setValue('editData', data));
                dispatch(setValue('data', data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Can't reactivate contact");
                }
                throw error;
            })
            .finally(() => {
                dispatch(setContactDetailsLoadingState(false));
            })
    }
}

export const restoreStaff = (staffId, tableId, isFilterActive) => {
    return (dispatch) => {
        dispatch(setContactDetailsLoadingState(true));

        return runUserProfileMutation(dispatch, 'restoreStaff', staffId)
            .then(data => {
                openSuccessNotification(`${data.fullName} is restored`);
                dispatch(changeTableRow(tableId, { ...data, adUser: data.user.adUser }, 'staffId', (isFilterActive === false || isFilterActive === "false")));
                dispatch(setValue('data', data));
                dispatch(setValue('editData', data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Error. Can't restore staff");
                }
                throw error;
            })
            .finally(() => {
                dispatch(setContactDetailsLoadingState(false));
            })
    }
}

export const setDefaultLoginRole = (value) => {
    return (dispatch) => {
        dispatch(setValue('editData.user.userRoleId', value));
        dispatch(setValue('data.user.userRoleId', value));
    }
}

export const canApplyUserName = (dispatch, userName, userId) => {
    return runUserProfileQuery(dispatch, 'canApplyUserName', { userName, userId })
        .catch(() => {
            return false;
        })
}

export const refreshPasswordResetDetails = (userId) => async (dispatch) => {
    try {
        const data = await runUserProfileQuery(dispatch, 'getDetailsByUserId', userId);
        if (data?.user?.userId !== userId) {
            throw new Error('The profile response did not contain the requested user');
        }
        dispatch(passwordResetDetailsReceived(data.user));
    } catch (error) {
        if (!isUnauthorizedError(error)) {
            openErrorNotification(`Can't refresh password status. Close and reopen the profile to see the latest status. ${getApiErrorMessage(error)}`);
        }
    }
};

const requestPassword = (endpointName, userId, successPrefix, errorMessage) => async (dispatch) => {
    dispatch(setContactDetailsLoadingState(true));
    try {
        const message = await runUserProfileMutation(dispatch, endpointName, { userId });
        if (typeof message !== 'string' || !message.startsWith(successPrefix)) {
            throw new Error(typeof message === 'string' ? message : errorMessage);
        }
        openSuccessNotification(message);
        await dispatch(refreshPasswordResetDetails(userId));
    } catch (error) {
        if (!isUnauthorizedError(error)) {
            openErrorNotification(getApiErrorMessage(error, errorMessage));
        }
    } finally {
        setTimeout(() => dispatch(setContactDetailsLoadingState(false)), config.LOADING_DELAY);
    }
};

export const resetUserPassword = (userId) => requestPassword(
    'resetPassword', userId, 'Request has been sent.', "Can't send password reset link"
);

export const sendTemporaryPassword = (userId) => requestPassword(
    'sendTemporaryPassword', userId, 'Temporary password sent.', "Can't send temporary password"
);

export const getCurrentUserEmail = () => {
    return (dispatch) => {
        runUserProfileQuery(dispatch, 'getUserEmail')
            .then(data => {
                dispatch(setValue('email', data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification(`Can't receive your email. Please reload page`);
                }
            })
    }
}

export const setDefaultUserInfo = () => {
    return (dispatch) => {
        dispatch(setEditDataToDefault());
    }
}
